// HistoryFeed (job page round): a record's history, newest first, grouped by day ("Today · Sun Oct 25"):
// what can be written into it on top (a Composer), then which kinds to show with their counts, a search,
// the pinned note, and the latest few rows with Show all under them. "Key moments" reads oldest first,
// as a story. A search that finds nothing says so and offers to clear it.
// Jumping to a row (hitId: a stop on the job's strip) shows it if it was past the latest few, shades it
// and gives it the focus. Reads only --feed-*; HistoryItem, ChoiceGroup, Field and Link read their own.
// Round 2, opens in place (Amir 26 Oct, pages/job-history-option): given renderEntry, every row opens its
// entry under it, several at once, each kept open across tabs and searches (openIds, which the page may
// hold itself to keep them in its address); "Collapse all n" in the bar once two or more are open; ↑ ↓
// move between rows, Home and End to the first and last; a jump opens its row as well as shading it,
// moving to All when the tab would hide it.
import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Choice, ChoiceGroup } from '../ChoiceGroup/ChoiceGroup.tsx';
import { TextField } from '../Field/Field.tsx';
import { HistoryItem, type HistoryKind } from '../HistoryItem/HistoryItem.tsx';
import { Link } from '../Link/Link.tsx';
import { StatusBar } from '../StatusBar/StatusBar.tsx';
import './HistoryFeed.css';

export interface HistoryEntry {
  id: string;
  /** When, local: "2026-10-25T08:20". */
  at: string;
  kind: HistoryKind;
  /** Who did what, with names in bold. */
  head: ReactNode;
  /** The words a search looks through: the head, detail and quote as plain text. */
  text: string;
  detail?: ReactNode;
  diff?: [string, string];
  quote?: string;
  extra?: ReactNode;
  /** One of the moments that tell the story. */
  isKey?: boolean;
}

export interface HistoryFilter {
  id: string;
  label: string;
  /** The kinds it shows; none shows all. */
  kinds?: HistoryKind[];
  /** Only the key moments, oldest first. */
  keyOnly?: boolean;
}

export interface HistoryFeedProps {
  entries: HistoryEntry[];
  pinned?: HistoryEntry;
  filters: HistoryFilter[];
  /** Today, "2026-10-25": its day group says Today. */
  today: string;
  /** How many show before Show all (5). */
  cap?: number;
  /** The row to jump to. */
  hitId?: string | null;
  /** On top: what can be written into it. */
  composer?: ReactNode;
  /** Its search's label ("Search the history"). */
  searchLabel?: string;
  /** Round 2: each row's head opens its detail. */
  onOpen?: (id: string) => void;
  /** Key moments read oldest first, as a story (round 1), or newest first like every other view (round 2). */
  keyOrder?: 'oldest' | 'newest';
  /** Round 2: the filter it opens on ("all" from the strip's Full history); the first when not given. */
  initialFilter?: string;
  /**
   * How the kinds are picked: choices (round 1), or tabs (round 2: the board's StatusBar, ink with an
   * underline and counts; the first two filters, Key moments and All, then a rule, then the kinds).
   */
  filterLook?: 'choices' | 'tabs';
  /** Opens in place: what an open row shows under it (its id → EntryDetail inline). */
  renderEntry?: (id: string) => ReactNode;
  /** The rows open now (held by the page, e.g. in its address); none: the feed holds them. */
  openIds?: readonly string[];
  /** Open on arrival when the feed holds them itself. */
  defaultOpenIds?: readonly string[];
  onOpenChange?: (ids: string[]) => void;
  /** Whether a row has more to show; one that hasn't is no button and has no chevron. */
  hasEntry?: (id: string) => boolean;
  /** Bumped to jump to the same row again. */
  hitSeq?: number;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "Sun Oct 25", the weekday worked out from the date. */
export function dayWords(date: string): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const wd = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return `${WEEKDAYS[wd]} ${MONTHS[m - 1]} ${d}`;
}
/** "8:20 a.m." */
export function timeWords(at: string): string {
  const [h, mi] = at.slice(11, 16).split(':').map(Number) as [number, number];
  return `${h % 12 || 12}:${String(mi).padStart(2, '0')} ${h >= 12 ? 'p.m.' : 'a.m.'}`;
}

export function HistoryFeed({
  entries,
  pinned,
  filters,
  today,
  cap = 5,
  hitId,
  composer,
  searchLabel = 'Search the history',
  onOpen,
  keyOrder = 'oldest',
  filterLook = 'choices',
  initialFilter,
  renderEntry,
  openIds,
  defaultOpenIds,
  onOpenChange,
  hasEntry,
  hitSeq = 0,
}: HistoryFeedProps) {
  const [filter, setFilter] = useState(initialFilter ?? filters[0]?.id ?? 'all');
  const [q, setQ] = useState('');
  const [all, setAll] = useState(false);
  const hitRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const [ownOpen, setOwnOpen] = useState<string[]>(() => [...(defaultOpenIds ?? [])]);
  const opened = openIds ?? ownOpen;
  const setOpened = (ids: string[]) => {
    if (!openIds) setOwnOpen(ids);
    onOpenChange?.(ids);
  };
  const toggle = (id: string) =>
    setOpened(opened.includes(id) ? opened.filter((x) => x !== id) : [...opened, id]);

  // newest first; rows of the same minute keep the order given (QA-815: a sort that called the tie
  // "newer" each way reversed them)
  const newest = useMemo(
    () => [...entries].sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0)),
    [entries],
  );
  const counts = useMemo(
    () =>
      Object.fromEntries(
        filters.map((f) => [
          f.id,
          newest.filter((e) => (f.keyOnly ? e.isKey : !f.kinds || f.kinds.includes(e.kind))).length,
        ]),
      ),
    [filters, newest],
  );

  // a jump shows everything, so the row is there, and clears what would hide it; opening in place, it
  // keeps the tab when the row is in it, else moves to All, and opens the row
  const [jumped, setJumped] = useState<string | null>(null);
  const jump = hitId ? `${hitId}#${hitSeq}` : null;
  if (jump && jump !== jumped) {
    setJumped(jump);
    const shows = (fid: string) => {
      const x = filters.find((y) => y.id === fid);
      const e = newest.find((y) => y.id === hitId);
      return !!x && !!e && (x.keyOnly ? !!e.isKey : !x.kinds || x.kinds.includes(e.kind));
    };
    if (renderEntry) {
      if (!shows(filter))
        setFilter(filters.find((x) => !x.keyOnly && !x.kinds)?.id ?? filters[0]?.id ?? 'all');
    } else setFilter(filters[0]?.id ?? 'all');
    setQ('');
    const i = newest.findIndex((e) => e.id === hitId);
    if (i >= cap) setAll(true);
  }
  const openRef = useRef({ opened, setOpened });
  openRef.current = { opened, setOpened };
  useEffect(() => {
    if (!hitId) return;
    // the jump opens its row (after the render that moved the tab, so the row is there)
    const o = openRef.current;
    if (renderEntry && !o.opened.includes(hitId)) o.setOpened([...o.opened, hitId]);
    const el = hitRef.current;
    el?.scrollIntoView({ block: 'center' });
    (el?.querySelector<HTMLElement>('.fl-hist__row') ?? el)?.focus({ preventScroll: true });
  }, [hitId, hitSeq]);

  const f = filters.find((x) => x.id === filter) ?? filters[0];
  const qq = q.trim().toLowerCase();
  let list = newest.filter(
    (e) =>
      (!f || (f.keyOnly ? e.isKey : !f.kinds || f.kinds.includes(e.kind))) &&
      (!qq || e.text.toLowerCase().includes(qq)),
  );
  if (f?.keyOnly && keyOrder === 'oldest') list = [...list].reverse();
  const total = list.length;
  const shown = all || f?.keyOnly || qq ? list : list.slice(0, cap);

  const rows: ReactNode[] = [];
  let lastDay = '';
  for (const e of shown) {
    const d = e.at.slice(0, 10);
    if (d !== lastDay) {
      rows.push(
        <h3 key={`d-${d}`} className="fl-feed__day">
          {d === today ? `Today · ${dayWords(d)}` : dayWords(d)}
        </h3>,
      );
      lastDay = d;
    }
    rows.push(
      <HistoryItem
        key={e.id}
        id={`h-${e.id}`}
        ref={e.id === hitId ? hitRef : undefined}
        kind={e.kind}
        time={timeWords(e.at)}
        detail={e.detail}
        diff={e.diff}
        quote={e.quote}
        extra={e.extra}
        isHit={e.id === hitId}
        onOpen={onOpen && !renderEntry ? () => onOpen(e.id) : undefined}
        {...(renderEntry && (hasEntry?.(e.id) ?? true)
          ? {
              onToggle: () => toggle(e.id),
              isOpen: opened.includes(e.id),
              expanded: opened.includes(e.id) ? renderEntry(e.id) : null,
            }
          : {})}
      >
        {e.head}
      </HistoryItem>,
    );
  }

  // ↑ ↓ between the rows' heads, past an open entry's links; Home and End to the first and last
  const onListKey = (ev: KeyboardEvent<HTMLDivElement>) => {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(ev.key)) return;
    const t = ev.target as HTMLElement;
    if (!t.classList.contains('fl-hist__row')) return;
    const heads = [...ev.currentTarget.querySelectorAll<HTMLElement>('.fl-hist__row')];
    const i = heads.indexOf(t);
    const j =
      ev.key === 'ArrowDown'
        ? Math.min(i + 1, heads.length - 1)
        : ev.key === 'ArrowUp'
          ? Math.max(i - 1, 0)
          : ev.key === 'Home'
            ? 0
            : heads.length - 1;
    ev.preventDefault();
    heads[j]?.focus();
  };
  const openShown = renderEntry ? shown.filter((e) => opened.includes(e.id)).length : 0;
  const collapseAll = () => {
    setOpened(opened.filter((id) => !shown.some((e) => e.id === id)));
    barRef.current
      ?.querySelector<HTMLElement>('[aria-checked="true"], [aria-pressed="true"], button')
      ?.focus();
  };

  return (
    <div className="fl-feed">
      {composer}
      <div className="fl-feed__bar" ref={barRef}>
        {filterLook === 'tabs' ? (
          // narrow (a phone), the row of tabs scrolls inside itself; the page never scrolls sideways
          <div className="fl-feed__tabs">
            <StatusBar
              label="Show in the history"
              oneRow
              selected={filter}
              onChange={setFilter}
              // one row of tabs, all drawn alike: Key moments and All first, then the kinds it has
              groups={[
                filters
                  .filter((x, i) => i < 2 || counts[x.id])
                  .map((x) => ({ id: x.id, label: x.label, count: counts[x.id] ?? 0 })),
              ]}
            />
          </div>
        ) : (
          <ChoiceGroup label="Show" selectionMode="single" value={filter} onChange={(v) => setFilter(v)}>
            {filters
              .filter((x) => counts[x.id] || x.id === filters[0]?.id)
              .map((x) => (
                <Choice key={x.id} value={x.id} count={counts[x.id]}>
                  {x.label}
                </Choice>
              ))}
          </ChoiceGroup>
        )}
        <div className="fl-feed__tools">
          {openShown >= 2 ? <Link onPress={collapseAll}>Collapse all {openShown}</Link> : null}
          <div className="fl-feed__search">
            <TextField
              type="search"
              label={searchLabel}
              hideLabel
              placeholder={searchLabel}
              value={q}
              onChange={setQ}
            />
          </div>
        </div>
      </div>
      <div className="fl-feed__list" aria-live={renderEntry ? undefined : 'polite'} onKeyDown={onListKey}>
        {pinned && !qq && f === filters[0] ? (
          <HistoryItem kind={pinned.kind} quote={pinned.quote} detail={pinned.detail} isPinned>
            {pinned.head}
          </HistoryItem>
        ) : null}
        {rows}
        {total === 0 ? (
          <p className="fl-feed__none">
            {qq ? `Nothing in the history matches “${q.trim()}”.` : 'Nothing of this kind yet.'}{' '}
            {qq ? <Link onPress={() => setQ('')}>Clear the search</Link> : null}
          </p>
        ) : null}
      </div>
      {total > shown.length ? (
        <p className="fl-feed__more">
          <Link onPress={() => setAll(true)}>Show all {total}</Link>
        </p>
      ) : all && total > cap && !f?.keyOnly && !qq ? (
        <p className="fl-feed__more">
          <Link onPress={() => setAll(false)}>Show the latest {cap}</Link>
        </p>
      ) : null}
    </div>
  );
}
